import React from 'react';
import { AlertTriangle, Info, AlertCircle } from 'lucide-react';
import Modal from '../Modal/Modal';
import ModalBody from '../Modal/ModalBody';
import ModalFooter from '../Modal/ModalFooter';
import Button from '../Button/Button';

/**
 * ConfirmDialog Component
 *
 * A modern confirmation dialog for destructive or important actions
 * following the AB Civil design system and 2025 UI standards.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Control dialog visibility
 * @param {function} props.onClose - Close handler (also called on cancel)
 * @param {function} props.onCancel - Cancel handler (alias for onClose)
 * @param {function} props.onConfirm - Confirm/action handler
 * @param {string} props.title - Dialog title
 * @param {string} props.message - Dialog message/description
 * @param {string} props.confirmText - Confirm button text (default: "Confirm")
 * @param {string} props.cancelText - Cancel button text (default: "Cancel")
 * @param {string} props.variant - Dialog variant: 'danger' | 'warning' | 'info' (default: 'danger')
 * @param {boolean} props.loading - Show loading state on confirm button
 * @param {number} props.zIndex - Custom z-index for nested modals
 */
const ConfirmDialog = ({
  isOpen = false,
  onClose,
  onCancel,
  onConfirm,
  title = 'Are you sure?',
  message = '',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger',
  loading = false,
  zIndex,
}) => {
  const handleClose = onCancel || onClose;
  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
  };

  // Get icon based on variant
  const getIcon = () => {
    switch (variant) {
      case 'danger':
        return <AlertTriangle size={24} />;
      case 'warning':
        return <AlertCircle size={24} />;
      case 'info':
        return <Info size={24} />;
      default:
        return <AlertTriangle size={24} />;
    }
  };

  // Get color based on variant
  const getColor = () => {
    switch (variant) {
      case 'danger':
        return 'var(--danger)';
      case 'warning':
        return 'var(--warning)';
      case 'info':
        return 'var(--info)';
      default:
        return 'var(--danger)';
    }
  };

  // Get button variant based on dialog variant
  const getButtonVariant = () => {
    switch (variant) {
      case 'danger':
        return 'danger';
      case 'warning':
        return 'primary'; // Orange for warnings
      case 'info':
        return 'secondary'; // Blue for info
      default:
        return 'danger';
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="sm" closeOnBackdrop={false} zIndex={zIndex}>
      {/* Custom Header with Icon */}
      <div
        style={{
          padding: 'var(--space-6)',
          borderBottom: '1px solid var(--gray-700)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-4)',
        }}
      >
        <div
          style={{
            width: '48px',
            height: '48px',
            borderRadius: 'var(--radius-full)',
            backgroundColor: `${getColor()}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            color: getColor(),
          }}
        >
          {getIcon()}
        </div>
        <div style={{ flex: 1 }}>
          <h2
            style={{
              margin: 0,
              fontSize: 'var(--text-xl)',
              fontWeight: 'var(--font-semibold)',
              color: 'var(--gray-50)',
            }}
          >
            {title}
          </h2>
        </div>
      </div>

      <ModalBody>
        <div
          style={{
            margin: 0,
            color: 'var(--gray-300)',
            fontSize: 'var(--text-base)',
            lineHeight: 'var(--leading-relaxed)',
          }}
        >
          {message}
        </div>
      </ModalBody>

      <ModalFooter>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 'var(--space-3)',
            width: '100%',
          }}
        >
          <Button variant="ghost" size="md" onClick={handleClose} disabled={loading}>
            {cancelText}
          </Button>
          <Button
            variant={getButtonVariant()}
            size="md"
            onClick={handleConfirm}
            loading={loading}
            disabled={loading}
          >
            {confirmText}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default ConfirmDialog;
